import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateFieldComponent),
      multi: true,
    },
  ],
})
export class DateFieldComponent
  implements OnInit, ControlValueAccessor, Validator
{
  public bsConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-default',
    isAnimated: true,
    dateInputFormat: 'YYYY-MM-DD HH:mm',
  };
  public data: Date = null;
  public onChangeFn: any = (_: any) => Date;
  public onTouchFn: any = () => {};
  public control: NgControl;
  public iconClassName = 'info-icon-two-action-buttons';
  private errorMessages = new Map<string, () => string>();

  @Input() settings: RRIInput<Date>;
  @Input() datepickerSettings?: Partial<BsDatepickerConfig>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() customErrorMessages: Map<string, () => string>;
  @Output() clearInput: EventEmitter<boolean> = new EventEmitter();

  constructor(private inj: Injector) {
    this.errorMessages.set('required', () => `Required`);
  }

  ngOnInit(): void {
    if (this.settings?.hideResetInput) {
      this.iconClassName = 'info-icon-no-clear-two-action-buttons';
    }
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['datepickerSettings'] &&
      changes['datepickerSettings'].previousValue !==
        changes['datepickerSettings'].currentValue
    ) {
      this.bsConfig = {
        ...this.bsConfig,
        ...this.datepickerSettings,
      };
      this.onChange();
    }
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = null;
      }
    }
    this.clearInput.emit(true);
    this.onChange();
  }
  public datepickerChanged(event): void {
    if (event && event instanceof Date && !isNaN(event.getTime())) {
      if (this.data != event) {
        this.data = event;
        this.onChange();
      }
    } else {
      if (event instanceof Date) {
        this.data = null;
        this.onChange();
      }
    }
  }

  public writeValue(value: Date): void {
    this.data = value;
  }

  public registerOnChange(fn: Date): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  public onChange() {
    this.onChangeFn(this.data);
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }
  public validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
